import { FilterAlt } from '@mui/icons-material';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BusinessCostSummaryFilter } from 'pages/admin/CostDashboard/hooks/useGetBusinessCostSummary';
import React, { useMemo, useState } from 'react';
import TRow from 'shared/components/Table/TRow';
import {
  BusinessCostRecord,
  BusinessCostSummaryReply,
} from 'shared/types/protoc-gen/bffbillsystem';
import { ColumnConfig } from 'shared/types/table';
import { commify } from 'shared/utils/strings';

import {
  BUSINESS_LIST_OPTIONS,
  CATEGORY_LIST_OPTIONS,
  COST_DASHBOARD_VARIANT,
  COST_SUMMARY_KINDS_OPTIONS,
  COST_SUMMARY_TYPES_OPTIONS,
  SUB_RESOURCE_LIST_OPTIONS,
} from '../../constants';
import CostFilterPopover from '../CostTableFilterPopover';

export type ResourceRankingFilter = Pick<
  BusinessCostSummaryFilter,
  'business' | 'kind' | 'type' | 'subResource'
>;

interface IResourceRankingTable extends ResourceRankingFilter {
  setResourceRankingFilter: React.Dispatch<React.SetStateAction<ResourceRankingFilter>>;
  businessCostSummary?: BusinessCostSummaryReply;
  variant: COST_DASHBOARD_VARIANT;
}

const isFilterableColumn = (id: string): id is keyof ResourceRankingFilter => {
  return ['business', 'kind', 'type', 'subResource'].includes(id);
};

const ResourceRankingTable: React.FC<IResourceRankingTable> = ({
  businessCostSummary,
  setResourceRankingFilter,
  variant,
}) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [activeColumn, setActiveColumn] = useState<ColumnConfig<BusinessCostRecord> | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const toggleGroup = (groupKey: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const resourceRankingDataGroupedData = useMemo(() => {
    if (!businessCostSummary) return {};

    return businessCostSummary.items.reduce((acc, item) => {
      const key = item.k8sAppName || 'unknown';

      if (!acc[key]) acc[key] = [];
      acc[key].push(item);

      return acc;
    }, {} as Record<string, BusinessCostRecord[]>);
  }, [businessCostSummary]);

  console.log('BUSINESS COST SUMMARY', businessCostSummary);

  const columnConfig: ColumnConfig<BusinessCostRecord>[] = useMemo(
    () => [
      {
        id: 'business',
        Header: variant === COST_DASHBOARD_VARIANT.ROLLUP ? 'Business' : 'Category',
        Cell: row => <Typography variant="caption">{row.business}</Typography>,
        filterable: true,
      },
      {
        id: 'name',
        Header: 'Name',
        Cell: row => <Typography variant="caption">{row.name}</Typography>,
        filterable: false,
      },
      {
        id: 'kind',
        Header: 'Kind',
        Cell: row => <Typography>{row.kind}</Typography>,
        filterable: true,
      },
      {
        id: 'type',
        Header: 'Type',
        Cell: row => <Typography>{row.type}</Typography>,
        filterable: true,
      },
      {
        id: 'subResource',
        Header: 'Sub Resource',
        Cell: row => <Typography>{row.subResource}</Typography>,
        filterable: true,
      },
      {
        id: 'cost',
        Header: 'Cost',
        Cell: row => <Typography>${commify(row.costCounter.toFixed(2))}</Typography>,
        align: 'right',
        filterable: false,
      },
    ],
    [variant],
  );

  const allValues = useMemo<Record<keyof ResourceRankingFilter, string[]>>(() => {
    return {
      kind: COST_SUMMARY_KINDS_OPTIONS.filter(opt => opt.value !== 'ALL').map(kind => kind.value),
      type: COST_SUMMARY_TYPES_OPTIONS.filter(opt => opt.value !== 'ALL').map(type => type.value),
      subResource: SUB_RESOURCE_LIST_OPTIONS.filter(opt => opt.value !== 'ALL').map(
        subResource => subResource.value,
      ),
      business:
        variant === COST_DASHBOARD_VARIANT.ROLLUP
          ? BUSINESS_LIST_OPTIONS.map(business => business.value)
          : CATEGORY_LIST_OPTIONS.map(category => category.value),
    };
  }, [variant]);

  const overallTotalCost = useMemo(() => {
    return Object.values(resourceRankingDataGroupedData)
      .flat()
      .reduce((sum, record) => sum + record.costCounter, 0);
  }, [resourceRankingDataGroupedData]);

  return (
    <Stack spacing={5}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columnConfig.map(col => (
                <TableCell align={col.align || 'left'} key={col.id} sx={{ width: col.width }}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    <Typography variant="subtitle2">{col.Header}</Typography>

                    {col.filterable && (
                      <Box
                        component="span"
                        onClick={e => {
                          setFilterAnchorEl(e.currentTarget);
                          setActiveColumn(col);
                        }}
                        sx={{ display: 'inline-flex', cursor: 'pointer' }}
                      >
                        <FilterAlt sx={{ width: 15, height: 15 }} />
                      </Box>
                    )}
                  </Stack>
                </TableCell>
              ))}

              {activeColumn && (
                <CostFilterPopover
                  allValues={isFilterableColumn(activeColumn.id) ? allValues[activeColumn.id] : []}
                  anchorEl={filterAnchorEl}
                  column={activeColumn}
                  onChange={newValues => {
                    setFilters(prev => ({ ...prev, [activeColumn.id]: newValues }));

                    setResourceRankingFilter(prev => ({
                      ...prev,
                      [activeColumn.id]: newValues.length === 0 ? 'ALL' : newValues.join(','),
                    }));
                  }}
                  onClose={() => {
                    setFilterAnchorEl(null);
                    setActiveColumn(null);
                  }}
                  open={Boolean(filterAnchorEl)}
                  selectedValues={filters[activeColumn.id] || []}
                  setResourceRankingFilter={setResourceRankingFilter}
                  variant={variant}
                />
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {Object.entries(resourceRankingDataGroupedData).map(([groupKey, records]) => (
              <React.Fragment key={groupKey}>
                <TableRow>
                  <TableCell colSpan={columnConfig.length} sx={{ p: 0 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      onClick={() => toggleGroup(groupKey)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: theme => theme.palette.grey[200],
                        ml: -5,
                        px: 2,
                        py: 2,
                        '&:hover': { backgroundColor: theme => theme.palette.grey[300] },
                      }}
                    >
                      <Typography>{groupKey}</Typography>

                      <Typography>
                        ${commify(records.reduce((sum, r) => sum + r.costCounter, 0).toFixed(2))}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>

                {openGroups[groupKey] &&
                  records.map(record => (
                    <TRow<BusinessCostRecord>
                      columnConfig={columnConfig}
                      key={record.id}
                      rowData={record}
                    />
                  ))}
              </React.Fragment>
            ))}

            {Object.keys(resourceRankingDataGroupedData).length > 0 && (
              <TableRow>
                <TableCell colSpan={columnConfig.length} sx={{ p: 0 }}>
                  <Stack
                    direction="row"
                    justifyContent="end"
                    sx={{
                      backgroundColor: theme => theme.palette.grey[200],
                      ml: -5,
                      px: 2,
                      py: 2,
                      '&:hover': { backgroundColor: theme => theme.palette.grey[300] },
                    }}
                  >
                    <Typography fontWeight="bold">{`Total Cost: $${commify(
                      overallTotalCost.toFixed(2),
                    )}`}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {Object.keys(resourceRankingDataGroupedData).length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={columnConfig.length}>
                  <Typography sx={{ py: 2 }} variant="caption">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default ResourceRankingTable;
